"use client"

import { ProductSiteRelatedBlogType } from "@/app/types/product-site";
import { Box, colors, NoSsr, Skeleton, Typography } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import dayjs from "@v2/lib/dayjs";
import { BookOpen } from "lucide-react";
import Link from "next/link";
import { memo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

export default function PdpRelatedBlogs({ blogs }: {
    blogs: ProductSiteRelatedBlogType[]
}) {
    return (
        <NoSsr fallback={<BlogsSkeleton />}>
            <Box sx={{ bgcolor: '#fff', mt: '4px', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 1.5 }}>
                    <Box sx={{ flexShrink: 0, display: 'flex' }}>
                        <BookOpen size={20} color={colors.grey[700]} />
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 500, color: colors.grey[800] }}>
                        مقالات مرتبط
                    </Typography>
                </Box>

                <Box sx={{ pb: 2 }}>
                    <Swiper
                        spaceBetween={16}
                        style={{ padding: '0px 16px 0px' }}
                        breakpoints={{
                            0:   { slidesPerView: 1.2 },
                            600: { slidesPerView: 2.2 },
                            900: { slidesPerView: 3 },
                        }}
                    >
                        {blogs.map((blog, index) => {
                            return (
                                <SwiperSlide key={`${index}-${blog.id}`}>
                                    <Box component={Link} href={`/fa/blog/${blog.slug}`} sx={{
                                        display: 'block',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: colors.grey[100],
                                        '&:hover h3': { color: colors.red[900], textDecoration: 'underline' },
                                    }}>
                                        <MediaV2
                                            media_id={blog.cover?.id}
                                            alt={blog.cover?.alt ?? blog.title}
                                            width={400}
                                            disablePlaceholder
                                            boxProps={{
                                                sx: {
                                                    width: '100%',
                                                    aspectRatio: '321 / 198',
                                                    '& > img': { pointerEvents: 'none' }
                                                }
                                            }}
                                            imageProps={{
                                                fill: true,
                                                loading: 'lazy',
                                                style: { objectFit: 'cover' }
                                            }}
                                        />
                                        <Box sx={{ p: 1.25 }}>
                                            <Typography component="h3" sx={{
                                                fontSize: { xs: 13, sm: 14 },
                                                fontWeight: 600,
                                                color: colors.grey[900],
                                                transition: '250ms',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                lineHeight: 1.6,
                                            }}>
                                                {blog.title}
                                            </Typography>
                                            <Box
                                                component="p"
                                                dangerouslySetInnerHTML={{ __html: blog.excerpt || '' }}
                                                sx={{
                                                    fontSize: { xs: 11, sm: 12 },
                                                    color: colors.grey[600],
                                                    mt: 0.5,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: 'block',
                                                    minHeight: '1.5em',
                                                    lineHeight: 1.5,
                                                }}
                                            />
                                            <Typography component="span" sx={{
                                                fontSize: { xs: 11, sm: 12 },
                                                color: colors.grey[500],
                                                display: 'block',
                                                mt: 0.5,
                                            }}>
                                                {blog.published_at ? dayjs(blog.published_at).locale('fa').format('DD MMMM YYYY') : null}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </SwiperSlide>
                            )
                        })}
                    </Swiper>
                </Box>
            </Box>
        </NoSsr>
    )
}

const BlogCardSkeleton = memo(() => (
    <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: colors.grey[100] }}>
        <Box sx={{ width: '100%', aspectRatio: '321 / 198' }}>
            <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
        </Box>
        <Box sx={{ p: 1.25 }}>
            <Skeleton variant="text" width="80%" height={22} />
            <Skeleton variant="text" width="60%" height={18} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width="40%" height={18} sx={{ mt: 0.5 }} />
        </Box>
    </Box>
))

const BlogsSkeleton = memo(() => (
    <Box sx={{ bgcolor: '#fff', mt: '4px', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 1.5 }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={120} height={28} />
        </Box>
        {/* gap=16px matches Swiper spaceBetween; calc: (100% - (n-1)*16px) / n */}
        <Box sx={{ pb: 2, px: 2, display: 'flex', gap: '16px', overflow: 'hidden' }}>
            {/* xs: 1.2 slides */}
            <Box sx={{ flex: '0 0 calc(100% / 1.2)', display: { sm: 'none' } }}>
                <BlogCardSkeleton />
            </Box>
            {/* sm: 2.2 slides — show 2 */}
            <Box sx={{ flex: '0 0 calc((100% - 16px) / 2.2)', display: { xs: 'none', sm: 'block', md: 'none' } }}>
                <BlogCardSkeleton />
            </Box>
            <Box sx={{ flex: '0 0 calc((100% - 16px) / 2.2)', display: { xs: 'none', sm: 'block', md: 'none' } }}>
                <BlogCardSkeleton />
            </Box>
            {/* md: 3 slides */}
            <Box sx={{ flex: '0 0 calc((100% - 32px) / 3)', display: { xs: 'none', md: 'block' } }}>
                <BlogCardSkeleton />
            </Box>
            <Box sx={{ flex: '0 0 calc((100% - 32px) / 3)', display: { xs: 'none', md: 'block' } }}>
                <BlogCardSkeleton />
            </Box>
            <Box sx={{ flex: '0 0 calc((100% - 32px) / 3)', display: { xs: 'none', md: 'block' } }}>
                <BlogCardSkeleton />
            </Box>
        </Box>
    </Box>
))
